import { useMemo, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { Button, Skeleton } from '@moneyskyline/ui';
import { HonestInline } from '@/components/HonestInline';
import { useShellContext } from '@/app/shell/AppShell';
import { scopeHref } from '@/app/shell/scope';
import { useRules, type RuleRow } from '@/api/hooks/useRules';
import { BusinessNav } from './BusinessNav';
import { NewConditionDialog } from './NewConditionDialog';
import { RecordsDisclaimer } from './RecordsDisclaimer';
import { RuleApplyResult } from './RuleApplyResult';

function windowLabel(rule: RuleRow): string {
  if (!rule.effective_from && !rule.effective_to) return 'any dates';
  return `${rule.effective_from ?? '—'} – ${rule.effective_to ?? '—'}`;
}

export function ConditionsPage() {
  const { band } = useShellContext();
  const { search } = useLocation();
  const [params, setParams] = useSearchParams();
  const selectedId = params.get('rule');
  const rulesQuery = useRules();
  const [newOpen, setNewOpen] = useState(false);
  const rules = rulesQuery.data ?? [];
  const selected = useMemo(
    () => (rulesQuery.data ?? []).find((rule) => rule.rule_id === selectedId) ?? null,
    [rulesQuery.data, selectedId],
  );
  const enabledCount = rules.filter((rule) => rule.enabled).length;
  const desktop = band === 'desktop';

  return (
    <section className="mx-auto w-full max-w-6xl px-4 py-6" aria-labelledby="conditions-heading">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Link to={scopeHref('/business', search)} className="text-sm text-link">‹ Business</Link>
          <h1 id="conditions-heading" tabIndex={-1} className="text-title font-semibold">
            Conditions
            {rulesQuery.data ? (
              <span className="ml-2 text-sm font-normal text-muted-foreground">
                {' '}· {rules.length} · {enabledCount} on
              </span>
            ) : null}
          </h1>
        </div>
        <Button type="button" onClick={() => setNewOpen(true)} className="min-h-11">
          + New condition
        </Button>
      </div>
      <BusinessNav conditionCount={rulesQuery.data ? rules.length : null} />

      {rulesQuery.isLoading && <Skeleton className="mt-4 h-24 w-full" />}
      {rulesQuery.isError && <HonestInline className="mt-4" kind="api-unavailable" />}
      {rulesQuery.data && rules.length === 0 && (
        <p role="status" className="mt-4 rounded-[10px] border border-border bg-card p-4 text-sm text-muted-foreground">
          No conditions yet. Preview the affected count before anything is applied.
        </p>
      )}

      <div className={desktop ? 'mt-4 grid grid-cols-[minmax(0,1fr)_460px] gap-6' : 'mt-4 flex flex-col gap-4'}>
        {rules.length > 0 && (
          <div className="overflow-hidden rounded-[10px] border border-border bg-card text-sm">
            <div className="hidden grid-cols-[44px_1fr_150px_130px] border-b border-border px-3 py-2 text-[11px] uppercase tracking-wide text-muted-foreground desktop:grid">
              <span>On</span><span>Condition</span><span>Window</span><span>Matching</span>
            </div>
            {rules.map((rule) => {
              const active = rule.rule_id === selectedId;
              return (
                <button
                  key={rule.rule_id}
                  type="button"
                  onClick={() => {
                    const next = new URLSearchParams(params);
                    next.set('rule', rule.rule_id);
                    setParams(next, { replace: true });
                  }}
                  className={['grid min-h-14 w-full grid-cols-1 items-center gap-1 border-b border-border px-3 py-2 text-left last:border-b-0 desktop:grid-cols-[44px_1fr_150px_130px]', active ? 'bg-muted' : ''].join(' ')}
                  aria-current={active ? 'true' : undefined}
                >
                  <span className="flex items-center gap-2 desktop:contents">
                    <span className={rule.enabled ? 'h-5 w-[34px] rounded-full bg-money-positive' : 'h-5 w-[34px] rounded-full bg-input'} aria-hidden />
                    <span>
                      <span className="font-semibold">{rule.restatement}</span>
                      <span className="block text-[12px] text-muted-foreground">
                        {rule.kind}
                        {rule.enabled ? '' : ' · off'}
                      </span>
                    </span>
                  </span>
                  <span className="text-[12px] text-muted-foreground desktop:text-sm">{windowLabel(rule)}</span>
                  <span className="font-mono text-sm">{rule.match_count}</span>
                </button>
              );
            })}
          </div>
        )}

        <div className="rounded-[12px] border border-border bg-card p-4 text-sm">
          {!selected && (
            <p className="text-muted-foreground">Select a condition to see examples currently matching.</p>
          )}
          {selected && (
            <>
              <div className="font-semibold">Edit · {selected.restatement}</div>
              <p className="mt-2 text-xs text-muted-foreground">
                Turning a condition on or off isn’t available on this build: the list contract has no version for a compare-and-set write. Hand labels are never overwritten by a rule.
              </p>
              <RuleApplyResult rule={selected} />
            </>
          )}
        </div>
      </div>

      <RecordsDisclaimer className="mt-6 max-w-xl" />
      <NewConditionDialog open={newOpen} onOpenChange={setNewOpen} />
    </section>
  );
}

export default ConditionsPage;
